import { Injectable } from '@angular/core';
import { LogService } from '@innovation-os-ng/core/logger';
import { getTenantSlug, RemoteStorageV2Service } from '@innovation-os-ng/core/utils';
import { firstValueFrom, ReplaySubject, Subject } from 'rxjs';
import { UserHistoryService } from '../contracts/user-history.contract';

const LAST_VISITED_TENANT_KEY = 'lastVisitedTenant';
const LAST_VISITED_SPACE_KEY = 'lastVisitedSpace';

@Injectable({
  providedIn: 'root'
})
export class RemoteSessionService implements UserHistoryService {
  private cachedLastVisitedSpace: { [tenantSlug: string]: Subject<string> } = {};
  private cachedLastVisitedTenant: string;

  constructor(private readonly remoteStorageService: RemoteStorageV2Service, private readonly logger: LogService) {}

  // TODO all this lastVisited logic in the auth module is a really odd place
  /**
   * Get last visited tenant from remote storage
   */
  public async getLastVisitedTenant(): Promise<string> {
    if (this.cachedLastVisitedTenant) {
      return this.cachedLastVisitedTenant;
    }

    try {
      const storageData = await firstValueFrom(this.remoteStorageService.get());
      // Extract lastVisitedTenant item from remote storage object and assign
      this.cachedLastVisitedTenant = storageData?.[LAST_VISITED_TENANT_KEY] as string;
      return this.cachedLastVisitedTenant;
    } catch (error) {
      this.logger.warn(error, {});
      return null;
    }
  }

  /**
   * Set the last visited tenant in remote storage
   *
   * @param tenantSlug
   */
  public async setLastVisitedTenant(tenantSlug: string): Promise<void> {
    // We save the last visited tenant on a tenant level,
    // So we need to only pass the tenantSlug and leave out both tenantUri and spaceUri as null
    // we take the first response from the remote storage data stream without waiting for observable to complete
    const storageData = await firstValueFrom(this.remoteStorageService.set(null, null, tenantSlug));

    // Extract lastVisitedSpace item from remote storage object and assign
    this.cachedLastVisitedTenant = storageData?.[LAST_VISITED_SPACE_KEY] as string;
  }

  /**
   * Get last visited space from remote storage
   * this request will get the space with the tenantSlug
   *
   * @param tenantUri (param deprecated)
   */
  public async getLastVisitedSpace(tenantUri?: string): Promise<string> {
    /**
     * Only show the warning message if "tenantUri" params is being used.
     */
    if (tenantUri) {
      console.warn(`tenantUri param is deprecated, please stop using it.
    tenantSlug can be extracted from the subdomain this reduces time requesting tenant info`);
    }

    const tenantSlug = getTenantSlug();
    return this.getLastVisitedSpaceBySlug(tenantSlug);
  }

  /**
   * Get last visited space from remote storage
   * this request will get the space with the tenantSlug
   *
   * @param tenantSlug
   */
  public async getLastVisitedSpaceBySlug(tenantSlug?: string): Promise<string> {
    const replaySubject = new ReplaySubject<string>();

    if (this.cachedLastVisitedSpace[tenantSlug]) {
      return firstValueFrom(this.cachedLastVisitedSpace[tenantSlug]);
    } else {
      // in order to avoid multiple call to remoteStorageService
      // we cache a replaySubject
      this.cachedLastVisitedSpace[tenantSlug] = replaySubject;
    }

    try {
      // Last visited space is on a tenant level,
      // So we need to only pass the tenantSlug and leave out both tenantUri and spaceUri as null
      // we take the first response from the remote storage data stream without waiting for observable to complete
      firstValueFrom(this.remoteStorageService.get(null, null, tenantSlug)).then((storageData) => {
        // Extract lastVisitedSpace item from remote storage object and assign
        this.cachedLastVisitedSpace[tenantSlug].next((storageData?.[LAST_VISITED_SPACE_KEY] as string) ?? null);
      });

      return firstValueFrom(this.cachedLastVisitedSpace[tenantSlug]);
    } catch (error) {
      this.logger.warn(error, {});
      return new Promise(null);
    }
  }

  /**
   * Set the last visited space in remote storage
   *
   * @param spaceUri
   * @param tenantUri (param deprecated)
   */
  public async setLastVisitedSpace(spaceUri: string, tenantUri?: string): Promise<void> {
    /**
     * Only show the warning message if "tenantUri" params is being used.
     */
    if (tenantUri) {
      console.warn(`tenantUri param is deprecated, please stop using it.
    tenantSlug can be extracted from the subdomain this reduces time requesting tenant info`);
    }

    const tenantSlug = getTenantSlug();

    // Last visited space is on a tenant level,
    // So we need to only pass the tenantSlug and leave out both tenantUri and spaceUri as null
    // we take the first response from the remote storage data stream without waiting for observable to complete
    const storageData = await firstValueFrom(this.remoteStorageService.set(LAST_VISITED_SPACE_KEY, spaceUri, null, null, tenantSlug));

    this.cachedLastVisitedSpace[tenantSlug] = new ReplaySubject();
    // Extract lastVisitedSpace item from remote storage object and assign
    this.cachedLastVisitedSpace[tenantSlug].next((storageData?.[LAST_VISITED_SPACE_KEY] as string) ?? null);
  }
}
